import React, { Component } from "react";
import { Col, Row, Table, notification } from "antd";
import { ContentWrapper, Icon } from "components";
import { Helmet } from "react-helmet";
import { connect } from "react-redux";
import { delayedDispatch, setBreadcrumb, setLoader, setTitle } from "store/actions";
import { API, Endpoints } from "utils/api";
import strings from "utils/strings";
import { formatPrice, getSuccessLevelBadge } from "utils/utils";
import moment from "moment";
import { getColumnFilter } from "utils/comp-utils";
import { DateTime } from "luxon";

class Special extends Component<any, any> {
	searchTimeout: NodeJS.Timeout | undefined;

	constructor(props: any) {
		super(props);

		this.state = {
			data: [],
			startDate: moment().add({ months: -5 }).startOf("month"),
			endDate: moment().endOf("month")
		};
	}

	async componentDidMount(): Promise<void> {
		const { dispatch } = this.props;

		dispatch(setTitle(""));

		await this.getData();
		this.breadcrumb();
	}

	breadcrumb() {
		delayedDispatch(
			setBreadcrumb(() => {
				const { startDate, endDate, partnerOptions } = this.state;

				return {
					locations: [
						{
							text: strings.commissions.special,
							icon: "organization"
						}
					],
					actions: [
						{
							type: "datePicker",
							text: strings.dashboard.period,
							dates: [startDate, endDate],
							monthly: true,
							onChange: this.handleSelect,
							className: "fixedPicker"
						}
					]
				};
			})
		);
	}

	async getData() {
		const { startDate, endDate } = this.state;
		const { dispatch } = this.props;

		dispatch(setLoader(true));

		const body = { type: "special", startDate, endDate };

		try {
			const response = await API.post({
				url: Endpoints.uriCommissions("search-by-type"),
				data: body
			});

			if (response.ok) {
				const { partner, eligible = false, settings, commissions, commissionsChart } = response.data.results;

				this.setState({ partnerDetail: partner, eligible, settings, commissions, commissionsChart });
			} else {
				notification.error({
					message: strings.commissions.special,
					description: response?.data?.message || strings.serverErrors.wentWrong,
					placement: "bottomRight",
					duration: 5
				});
			}
		} catch (err) {
			notification.error({
				message: strings.serverErrors.title,
				description: err as string,
				placement: "bottomRight",
				duration: 5
			});
		}

		dispatch(setLoader(false));
	}

	handleSelect = (dates: any) => {
		const newDates = [];
		if (dates && dates[0]) {
			const start = moment(dates[0]);
			newDates.push(start.startOf("month"));
		}

		if (dates && dates[1]) {
			const end = moment(dates[1]);
			newDates.push(end.endOf("month"));
		}

		if (newDates[0] && newDates[1] && newDates[0].isSame(newDates[1], "month")) {
			newDates[0] = moment(newDates[0]).startOf("month");
			newDates[1] = moment(newDates[1]).endOf("month");
		}

		this.setState(
			{
				startDate: newDates[0],
				endDate: newDates[1]
			},
			() => this.getData()
		);
	};

	renderPartner() {
		const { partnerDetail, eligible, settings } = this.state;
		const { bonuses } = settings || {};
		const { special } = bonuses || {};

		if (!special) return null;

		return (
			<Row gutter={[20, 20]}>
				{!eligible && (
					<Col xs={24}>
						<ContentWrapper>
							<div className="ScreenHeader">
								<div className="ScreenHeaderLeft">
									<Icon name="user" />
									<h2>{strings.commissions.eligibility}</h2>
								</div>
							</div>
							<p>
								<strong>
									{strings.commissions.actualRank}: {getSuccessLevelBadge(partnerDetail?.partner?.successLevel)}
								</strong>
							</p>
							<p className="CommissionsPartnersAttention">{strings.commissions.notEligibleSpecial}</p>
						</ContentWrapper>
					</Col>
				)}
				<Col xs={24}>
					<ContentWrapper>
						<div className="ScreenHeader">
							<div className="ScreenHeaderLeft">
								<Icon name="points" />
								<h2>{strings.fields.description}</h2>
							</div>
						</div>
						<p className="CommissionsPartnersAttention">{strings.commissions.specialDescription}</p>
					</ContentWrapper>
				</Col>
				<Col xs={24}>
					<ContentWrapper>
						<div className="ScreenHeader">
							<div className="ScreenHeaderLeft">
								<Icon name="points" />
								<h2>{strings.commissions.special}</h2>
							</div>
						</div>
						<Row>
							<Col xs={24} md={12}>
								<table className="CommissionTpvTable">
									<thead>
										<tr>
											<th>{strings.settings.successLevel}</th>
											<th>{strings.generic.bonus}</th>
										</tr>
									</thead>
									<tbody>
										{Object.entries(special)?.map((entry: any, index: number) => {
											const rank = partnerDetail?.partner?.rewards?.[entry[0]];
											const reached = rank?.achieved;

											const style: React.CSSProperties = {};
											if (reached) {
												style.backgroundColor = "#00b5b033";
												style.fontWeight = 600;
												style.color = "#00b5b0";
											}

											return (
												<tr key={index} style={style}>
													<td>{getSuccessLevelBadge(entry[0])}</td>
													<td>{formatPrice(entry[1])}</td>
												</tr>
											);
										})}
									</tbody>
								</table>
							</Col>
						</Row>
					</ContentWrapper>
				</Col>
			</Row>
		);
	}

	renderMovements() {
		const { commissions = [] } = this.state;

		return (
			<Row gutter={[20, 20]}>
				<Col xs={24}>
					<ContentWrapper>
						<div className="ScreenHeader --multi">
							<div className="ScreenHeaderLeft">
								<Icon name="diagram" />
								<h2>{strings.commissions.special}</h2>
							</div>
							<div className="ScreenHeaderRight">
								<p>
									{strings.formatString(
										strings.wallet.earnedPeriod,
										formatPrice(commissions.reduce((acc: number, entry: any) => acc + entry.value, 0))
									)}
								</p>

								{/* <button className="EthicAddButton" onClick={this.exportData}>
									<Icon name="print" />
								</button> */}
							</div>
						</div>
						<Table
							style={{ marginTop: 20 }}
							scroll={{ x: "max-content" }}
							columns={[
								{
									title: strings.fields.date,
									dataIndex: "date",
									key: "date",
									render: (date: string) => DateTime.fromISO(date).toFormat("dd/MM/yyyy HH:mm"),
									defaultSortOrder: "descend",
									sorter: (a, b) => DateTime.fromISO(a.date).toMillis() - DateTime.fromISO(b.date).toMillis(),
									width: 150
								},
								{
									title: strings.users.partnerRankingAtTheTime,
									dataIndex: "actualSuccessLevel",
									key: "actualSuccessLevel",
									render: (_: any, record: any) => getSuccessLevelBadge(record?.actualSuccessLevel) || "-",
									sorter: (a, b) => a?.actualSuccessLevel?.localeCompare(b?.actualSuccessLevel),
									...getColumnFilter(["actualSuccessLevel"])
								},
								{
									title: strings.generic.bonus,
									dataIndex: "rankTag",
									key: "rankTag",
									render: (_: any, record: any) => getSuccessLevelBadge(record?.rankTag) || "-",
									sorter: (a, b) => a?.rankTag?.localeCompare(b?.rankTag),
									...getColumnFilter(["rankTag"])
								},
								{
									title: strings.commissions.valueToReceive,
									dataIndex: "totalValueToReceive",
									key: "totalValueToReceive",
									render: (totalValueToReceive: number) => formatPrice(totalValueToReceive || 0),
									sorter: (a, b) => a.totalValueToReceive - b.totalValueToReceive
								},
								{
									title: strings.commissions.installmentNumber,
									dataIndex: "installmentNumber",
									key: "installmentNumber",
									render: (installmentNumber: number) => installmentNumber || 0,
									sorter: (a, b) => a.installmentNumber - b.installmentNumber
								},
								{
									title: strings.commissions.installmentAmount,
									dataIndex: "value",
									key: "value",
									render: (value: number) => formatPrice(value || 0),
									sorter: (a, b) => a.value - b.value
								},
								{
									title: strings.commissions.installmentValueLeft,
									dataIndex: "valueLeftToReceive",
									key: "valueLeftToReceive",
									render: (valueLeftToReceive: number) => formatPrice(valueLeftToReceive || 0),
									sorter: (a, b) => a.valueLeftToReceive - b.valueLeftToReceive
								},
								{
									title: strings.generic.expirationDate,
									dataIndex: "expirationDate",
									key: "expirationDate",
									render: (expirationDate: string) => DateTime.fromISO(expirationDate).toFormat("dd/MM/yyyy HH:mm"),
									defaultSortOrder: "descend",
									sorter: (a, b) =>
										DateTime.fromISO(a.expirationDate).toMillis() - DateTime.fromISO(b.expirationDate).toMillis(),
									width: 150
								}
							]}
							dataSource={commissions}
							rowKey={(record) => record._id}
						/>
					</ContentWrapper>
				</Col>
			</Row>
		);
	}

	render() {
		return (
			<div className="CommissionSpecial">
				<Helmet>
					<title>{strings.commissions.special}</title>
					<meta name="description" content="Description of special commissions" />
				</Helmet>
				{this.renderPartner()}
				{this.renderMovements()}
			</div>
		);
	}
}

const mapStateToProps = (state: any) => ({
	language: state.language,
	countries: state.countries
});

export default connect(mapStateToProps)(Special);
